// The marks of the five monster kinds (mockup round 109, R2719,
// ui-map-interface.md §6.1.15): the 26 px mark of each kind, which the
// monster layer draws in the zoomed-out view, and which the legend and the
// lair point draw too.
//
// **One kind, one mark.** The mark of a kind is the body of its first card
// at rest, bow east, in the 26 px box of the NPC marks (`npc_mark_px`,
// R2637): the Hinged Jaw, the Trapdoor shown, the Quill Spine, the
// Discharge Ray and the Dot Cloud. The five read apart by shape alone in
// the NPC ink, as the page asks. The ragged ring `npc:monster` of R2637
// stays the side mark: the legend row NPC SIDE and the head of a monster
// card keep it.
//
// **One geometry, two surfaces.** The map draws a mark through the batch
// pen of the layer; the legend and a card draw it as SVG through
// `MarkSvgPen`, which writes the same capsules and circles as SVG elements.
//
// This is a leaf (`render/leaves.test.ts`).

import { card as S } from '../gen/strings';
import {
  DARK, LIGHT, MONSTER_BODIES, TransformPen, newMonsterState, restState,
  type MonsterInk, type MonsterPen, type MonsterState,
} from './monster-bodies';

export { MONSTER_CLASSES, monsterKindOf } from './monster-kinds';

/** The class word of each kind (`cls_*` of `[ui.card]`). */
export const MONSTER_CLASS_WORDS: readonly string[] = [
  S.cls_gulper, S.cls_murkhide, S.cls_quillback, S.cls_thrummer, S.cls_myriad,
];

/** The design names by kind, in the card order of round 109 (`designs` of
 *  `[[npc.monsters]]`, `mdesign_*` of `[ui.card]`). */
export const MONSTER_DESIGN_WORDS: readonly (readonly string[])[] = [
  [S.mdesign_hinged_jaw, S.mdesign_ring_maw, S.mdesign_ram_plate, S.mdesign_gullet_sack],
  [S.mdesign_trapdoor, S.mdesign_mantis_fold, S.mdesign_ink_veil, S.mdesign_lure_lamp],
  [S.mdesign_quill_spine, S.mdesign_brood_sac, S.mdesign_siphon_tube, S.mdesign_bud_gourd],
  [S.mdesign_discharge_ray, S.mdesign_twin_drum, S.mdesign_coil_eel, S.mdesign_lamp_crown],
  [S.mdesign_dot_cloud, S.mdesign_ribbon_school, S.mdesign_ring_pack, S.mdesign_comma_flock],
];

/** The design name of a kind and a design, or '' for one the table lacks. */
export function monsterDesignWord(kind: number, design: number): string {
  return MONSTER_DESIGN_WORDS[kind]?.[design] ?? '';
}

/** The side of the mark box, in local units: the 26 px of `npc_mark_px`
 *  at a mark scale of 1. */
export const MONSTER_MARK_BOX = 26;

/** One reused state at rest for the marks. */
const REST: MonsterState = newMonsterState();

/** A pen that keeps the box of what it is handed: it measures a mark. */
class BoxPen extends TransformPen {
  x0 = Infinity; y0 = Infinity; x1 = -Infinity; y1 = -Infinity;
  private see(x: number, y: number, r: number): void {
    this.x0 = Math.min(this.x0, x - r); this.x1 = Math.max(this.x1, x + r);
    this.y0 = Math.min(this.y0, y - r); this.y1 = Math.max(this.y1, y + r);
  }
  protected emitCap(x0: number, y0: number, x1: number, y1: number, w: number): void {
    this.see(x0, y0, w / 2); this.see(x1, y1, w / 2);
  }
  protected emitDisc(x: number, y: number, r: number): void { this.see(x, y, r); }
  protected emitRing(x: number, y: number, r: number, w: number): void { this.see(x, y, r + w / 2); }
  protected emitArc(x: number, y: number, r: number, w: number): void { this.see(x, y, r + w / 2); }
  protected emitDashed(x: number, y: number, r: number, w: number): void { this.see(x, y, r + w / 2); }
  protected emitGlow(): void { /* a glow has no edge */ }
}

/** The fit of each kind's mark in the box: the centre x, y and the scale. */
const FIT: Float64Array[] = [];

function fitOf(kind: number): Float64Array {
  let f = FIT[kind];
  if (f === undefined) {
    const p = new BoxPen();
    p.place(0, 0, 0, 1);
    restState(REST);
    // The measure runs at full alpha: `restState` keeps the alpha of the last
    // mark, and a pen draws nothing below its alpha floor.
    REST.alpha = 1;
    MONSTER_BODIES[kind][0](p, REST);
    const w = p.x1 - p.x0, h = p.y1 - p.y0;
    const k = Math.min(1, MONSTER_MARK_BOX / Math.max(w, h, 1));
    f = Float64Array.of((p.x0 + p.x1) / 2, (p.y0 + p.y1) / 2, k);
    FIT[kind] = f;
  }
  return f;
}

/**
 * The mark of a kind through a pen: the body of its first card at rest,
 * centred in the 26 px box and scaled down where the body is longer than
 * the box. The pen must already stand at the mark's place and bow.
 */
export function drawKindMark(kind: number, p: MonsterPen, alpha = 1): void {
  const bodies = MONSTER_BODIES[kind];
  if (!bodies) return;
  const f = fitOf(kind);
  restState(REST);
  REST.t = 0;
  REST.alpha = alpha;
  p.push(-f[0] * f[2], -f[1] * f[2], 0, f[2]);
  bodies[0](p, REST);
  p.pop();
}

const fig = (n: number): string => (Math.round(n * 100) / 100).toString();

/**
 * A pen that writes SVG elements: a capsule is a round-capped line, a disc a
 * filled circle, a ring and an arc a stroked circle or path. The two inks are
 * the classes `mm-l` and `mm-d`, which the stylesheet colours `--npc` and
 * `--npc-edge`. A glow writes nothing: a mark of 14 px has no room for it.
 */
export class MarkSvgPen extends TransformPen {
  private out: string[] = [];

  /** Takes the markup written so far, and starts again. */
  take(): string {
    const s = this.out.join('');
    this.out = [];
    return s;
  }

  private cls(ink: MonsterInk): string { return ink === DARK ? 'mm-d' : 'mm-l'; }

  protected emitCap(x0: number, y0: number, x1: number, y1: number, w: number, ink: MonsterInk, a: number): void {
    this.out.push(`<line class="${this.cls(ink)}" x1="${fig(x0)}" y1="${fig(y0)}" x2="${fig(x1)}" y2="${fig(y1)}"`
      + ` stroke-width="${fig(w)}" stroke-opacity="${fig(a)}"/>`);
  }

  protected emitDisc(x: number, y: number, r: number, ink: MonsterInk, a: number): void {
    this.out.push(`<circle class="${this.cls(ink)}f" cx="${fig(x)}" cy="${fig(y)}" r="${fig(r)}" fill-opacity="${fig(a)}"/>`);
  }

  protected emitRing(x: number, y: number, r: number, w: number, ink: MonsterInk, a: number): void {
    this.out.push(`<circle class="${this.cls(ink)}" cx="${fig(x)}" cy="${fig(y)}" r="${fig(r)}"`
      + ` stroke-width="${fig(w)}" stroke-opacity="${fig(a)}"/>`);
  }

  protected emitArc(x: number, y: number, r: number, w: number, start: number, sweep: number, ink: MonsterInk, a: number): void {
    const x0 = x + Math.cos(start) * r, y0 = y + Math.sin(start) * r;
    const x1 = x + Math.cos(start + sweep) * r, y1 = y + Math.sin(start + sweep) * r;
    this.out.push(`<path class="${this.cls(ink)}" d="M${fig(x0)} ${fig(y0)}A${fig(r)} ${fig(r)} 0 ${Math.abs(sweep) > Math.PI ? 1 : 0} ${sweep > 0 ? 1 : 0} ${fig(x1)} ${fig(y1)}"`
      + ` stroke-width="${fig(w)}" stroke-opacity="${fig(a)}"/>`);
  }

  protected emitDashed(x: number, y: number, r: number, w: number, count: number, fill: number, start: number, ink: MonsterInk, a: number): void {
    const len = (2 * Math.PI * r) / Math.max(1, count);
    this.out.push(`<circle class="${this.cls(ink)}" cx="${fig(x)}" cy="${fig(y)}" r="${fig(r)}" stroke-width="${fig(w)}"`
      + ` stroke-dasharray="${fig(len * fill)} ${fig(len * (1 - fill))}" stroke-dashoffset="${fig(-start * r)}" stroke-opacity="${fig(a)}"/>`);
  }

  protected emitGlow(): void { /* no glow in a small mark */ }
}

/** The SVG of the mark of each kind, written once. */
const KIND_SVG: string[] = [];

/**
 * The mark of a kind as an inline SVG of `px` square, in the classes
 * `npc-mark` (the box) and `mm-l`/`mm-d` (the inks). '' for a kind the
 * table lacks.
 */
export function kindMarkSvg(kind: number, px = 14): string {
  if (kind < 0 || kind >= MONSTER_BODIES.length) return '';
  let body = KIND_SVG[kind];
  if (body === undefined) {
    const pen = new MarkSvgPen();
    pen.place(0, 0, 0, 1);
    pen.ew = 0.9;
    drawKindMark(kind, pen);
    body = pen.take();
    KIND_SVG[kind] = body;
  }
  const h = MONSTER_MARK_BOX / 2;
  return `<svg class="npc-mark mm" viewBox="${-h} ${-h} ${MONSTER_MARK_BOX} ${MONSTER_MARK_BOX}"`
    + ` width="${px}" height="${px}" aria-hidden="true">${body}</svg>`;
}

/** The two inks, for a reader that names them. */
export { DARK as MONSTER_DARK, LIGHT as MONSTER_LIGHT };
